import { IArticle, ITemplate } from '@/types'
import {
  IArtistSpotlightTemplate,
  IGoodThingsComeInThreeTemplate,
  IGuideTemplate,
  IWeekAheadTemplate,
} from '@/types/components/ITemplate'
import { getTemplateName } from 'utils/articles'
import { ArticleHeader } from './ArticleHeader'
import { ArtistSpotlight } from './templates/ArtistSpotlight'
import { GoodThingsIn3 } from './templates/GoodThingsIn3'
import { Guide } from './templates/Guide'
import { WeekAhead } from './templates/WeekAhead'

const getHeaderImageAspectRatio = (template: ITemplate) => {
  const templateName = getTemplateName(template)

  switch (templateName) {
    case 'guide':
    case 'artist-spotlight':
      return '16:9' as const
    case 'good-things-come-in-three':
      return '1:1' as const
    case 'week-ahead':
      return '2:1' as const
    default:
      return '4:3' as const
  }
}

const getHeaderDisplay = (template: ITemplate) => {
  const templateName = getTemplateName(template)

  switch (templateName) {
    case 'guide':
    case 'artist-spotlight':
      return 'lg:row'
    default:
      return 'column'
  }
}

export const ArticleTemplate = ({
  title,
  subheading,
  quote,
  articleImage,
  headerBgColor,
  template,
  category,
}: IArticle['attributes']) => {
  if (!template || template.length > 1)
    throw new Error('Article has multiple templates')

  return (
    <div className='flex w-full flex-col gap-10'>
      {getTemplateName(template[0]) === 'good-things-come-in-three' && (
        <ArticleHeader
          image={articleImage?.data}
          backgroundColor={headerBgColor}
          imageAspectRatio={getHeaderImageAspectRatio(template[0])}
          className='py-6 sm:py-8'
        />
      )}

      {getTemplateName(template[0]) !== 'good-things-come-in-three' && (
        <ArticleHeader
          title={title}
          subheading={subheading}
          quote={quote}
          category={category?.data.attributes.name}
          image={articleImage?.data}
          imageAspectRatio={getHeaderImageAspectRatio(template[0])}
          display={getHeaderDisplay(template[0])}
        />
      )}

      {template && <Template template={template[0]} />}
    </div>
  )
}

interface TemplateProps {
  template: ITemplate | null
}

const Template = ({ template }: TemplateProps) => {
  if (!template) return null

  switch (getTemplateName(template)) {
    case 'guide':
      return <Guide template={template as IGuideTemplate} />
    case 'week-ahead':
      return <WeekAhead template={template as IWeekAheadTemplate} />
    case 'artist-spotlight':
      return <ArtistSpotlight template={template as IArtistSpotlightTemplate} />
    case 'good-things-come-in-three':
      return (
        <GoodThingsIn3 template={template as IGoodThingsComeInThreeTemplate} />
      )
    default:
      throw new Error(`Unknown template: ${template.__component}`)
  }
}
